<template>
    <section v-if="profile" id="profile-hero-wrapper"
        class="tw-w-full tw-overflow-hidden tw-rounded tw-relative tw-shadow tw-mb-6 tw-bg-white">
        <div v-if="own_profile && !$user.impersonating" @click="goToSettings" class="tw-absolute tw-m-8 tw-top-0 tw-right-0 tw-z-10">
            <div
                class="tw-bg-white tw-text-gray-600 tw-font-semibold tw-block tw-rounded tw-text-center tw-py-3 tw-px-6 tw-select-none tw-cursor-pointer hover:tw-opacity-75">
                <i aria-hidden="true" class="fa fa-pencil tw-pr-3"></i> {{ $t('profile.edit_profile') }}
            </div>
        </div>
        <div v-if="profile.profile.header_image" id="profile-bg-img" class="w-full"
            :style="`background: url('${profile.profile.header_image}') center center / cover;`"></div>
        <div v-else id="profile-bg-img" class="w-full"
            style="background: url('https://leden.imu.nl/img/image.resized.jpg') center center / cover;"></div>
        <div>
            <div class="tw-block lg:tw-flex tw-bg-white">
                <div class="tw-flex-1 tw-relative tw-px-2">
                    <div id="profile-avatar"
                        :class="['tw-relative tw-left-0 tw-right-0 tw-m-auto tw-h-auto tw-rounded tw-p-1 tw-block tw-bg-white tw-shadow-lg', lb_position ? `leaderboard-${lb_position}` : '']">
                        <img v-if="profile.avatar" :src="profile.avatar" />
                        <img v-else src="css/img/avatar.png" />
                        <div class="tw-p-3"  v-if="plugins.gamification">
                            <div class="tw-flex tw-items-center">
                                <div class="karma-level bold-text light-text">Level {{ profile.gamification.level }}
                                </div>
                                <div class="karma-points bold-text light-text tiny-text tw-ml-auto">{{
                                        profile.gamification.xp
                                }} <i aria-hidden="true" class="far fa-trophy"></i></div>
                            </div>
                            <div id="progress-bar-outer"
                                v-tooltip="`${Math.ceil(profile.gamification.next - profile.gamification.xp)} ${$t('game.more')} ${$settings('points_title')} ${$t('game.next_level_needed')} ${profile.level + 1}!`">
                                <div id="progress-bar-inner" :style="`width: ${calculateProgressBar * 100}%`"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style="flex:4;">
                    <div id="profile-info-wrapper" style="padding-bottom:100px;">
                        <div class="tw-flex tw-w-full">
                            <div id="info-box" class="tw-flex-1">
                                <h3 class="tw-font-semibold tw-mb-5 tw-text-gray-600">{{ profile.firstname }} {{
                                        profile.lastname
                                }}</h3>
                                <span class="extra-light-text tw-mb-4 tw-inline-block">{{ profile.username }} </span>
                                <h4 class="secondary-c small-text">{{ profile.profile.company }}</h4>
                                <div class="profile-trophies-overview flex">
                                    <div class="trophy flex relative display-only"></div>
                                </div>
                                <achievedtrophies v-if="profile && plugins.gamification"
                                    class="profile-trophies-overview flex tw-justify-center tw-items-center md:tw-justify-start md:tw-items-start tw-mt-6"
                                    :user_id="profile.id">
                                </achievedtrophies>
                            </div>
                            <div v-if="user.id !== profile.id" id="action-buttons"
                                class="tw-flex tw-items-center tw-ml-auto">
                                <a class="tw-mr-4" :href="`/messages?uid=${profile.id}`" title="Stuur bericht">
                                    <div class="chat-button secondary-c hover-opacity">
                                        <i aria-hidden="true" class="far fa-comment"></i>
                                    </div>
                                </a>
                                <div @click="toggleFollow(profile.id)"
                                    :class="['follow-btn tw-w-40 tw-inline', profile.follow_status == 'pending' || profile.follow_status == 'accepted' ? 'ghost-button' : 'general-button']">
                                    <p v-if="profile.follow_status === 'pending'" class="follow-text"><span
                                            class="normal">{{ $t('game.following_pending') }}</span><span
                                            class="hover">{{ $t('game.unfollow') }}</span></p>
                                    <p v-else-if="profile.follow_status === 'accepted'" class="follow-text"><span
                                            class="normal">{{ $t('game.following_user') }}</span><span class="hover">{{
                                                    $t('game.unfollow')
                                            }}</span></p>
                                    <p v-else class="follow-text">{{ $t('game.follow_user') }}</p>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div class="tw-flex tw-block tw-bottom-0 tabs tw-mt-4 tw-absolute" @mousedown.left="onMouseDown"
                        ref="horizontal">

                        <profile-tab v-on="$listeners" :active_tab="active_tab" tab="about" :text="$t('profile.about')">
                        </profile-tab>
                        <profile-tab v-if="plugins.community || settings['menu_boards'] !== ''" v-on="$listeners" :active_tab="active_tab" tab="followers"
                            :text="$t('profile.follower')"></profile-tab>
                        <profile-tab v-if="plugins.community || settings['menu_boards'] !== ''" v-on="$listeners" :active_tab="active_tab" tab="following"
                            :text="$t('profile.following')"></profile-tab>
                        <profile-tab v-if="plugins.community || settings['menu_boards'] !== ''" v-on="$listeners" :active_tab="active_tab" tab="feed"
                            :text="$t('search.activity')"></profile-tab>
                        <profile-tab v-if="plugins.gamification" v-on="$listeners" :active_tab="active_tab"
                            tab="trophies" text="Trophies"></profile-tab>
                        <profile-tab v-if="plugins.groups" v-on="$listeners" :active_tab="active_tab" tab="groups"
                            :text="$t('navigation.groups')"></profile-tab>
                        <profile-tab v-if="user.id === profile.id" v-on="$listeners" :active_tab="active_tab"
                            tab="certificates" :text="$t('profile.certificates')"></profile-tab>
                    </div>
                </div>

            </div>
        </div>
    </section>
</template>

<script>
import Global from '../../mixins/Global'
import axios from 'axios';
import Tab from './Tab.vue';

export default {
    name: 'ProfileHeader',

    mixins: [Global],

    props: ['active_tab', 'profile', 'own_profile'],

    components: {
        'profile-tab': Tab,
    },

    data() {
        return {
            user,
            lb_position: '',
            left: 0,
            originX: 0,
            originLeft: 0,
        }
    },

    computed: {
        achievements() {
            return this.profile.achievements;
        },

        calculateProgressBar() {
            const pointsneedednext = this.profile.gamification.next;
            const pointsneededprev = this.profile.gamification.level - 1;
            if (this.profile.gamification.xp > 0 && (pointsneedednext - pointsneededprev != 0)) {
                return (this.profile.gamification.xp - pointsneededprev) / (pointsneedednext - pointsneededprev);
            }
            return 0;
        },

    },

    methods: {
        onMouseDown(e) {
            this.originX = e.pageX;
            this.originLeft = this.left;
            window.addEventListener("mouseup", this.onMouseUp);
            window.addEventListener("mousemove", this.onMouseMove);
        },

        onMouseUp() {
            window.removeEventListener("mouseup", this.onMouseUp);
            window.removeEventListener("mousemove", this.onMouseMove);
        },

        onMouseMove(e) {
            const offset = e.pageX - this.originX;
            const left = this.originLeft - offset;
            this.$refs.horizontal.style.transform = `translateX(${-left}px)`;
            this.left = left;
        },

        goToSettings() {
            this.$router.push({ name: 'profile.edit' });
        },

        getLBPosition() {
            axios.get(`/api/v3/leaderboards/streak/${this.profile.id}`).then((response) => {
                this.lb_position = response.data;
            });
        },

        toggleFollow(id) {
            if (!this.profile.follow_status) {
                axios.post(`/api/v3/follow`, { id: id }).then((response) => {
                    HuddleUser.update({
                        where: this.profile.id,
                        data: {
                            follow_status: response.data.data.status
                        }
                    });
                });
            } else {
                axios.delete(`/api/v3/follow/${id}?by=profile.id`).then((response) => {
                    HuddleUser.update({
                        where: this.profile.id,
                        data: {
                            follow_status: null
                        }
                    });
                });
            }
        },
        
    },

    mounted() {
        this.getLBPosition();
    }
}
</script>

<style lang="scss" scoped>
.tabs {
    margin-left: 50px;
}

#profile-bg-img {
    height: 175px;

    @screen lg {
        height: 275px;
    }
}

#profile-avatar {
    left: 0;
    right: 0;
    margin: auto;
    width: 181px;
    top: -285px;
    position: absolute;

    @screen lg {
        top: -100px;
        width: 186px;
    }

    img {
        width: 180px;
        height: 180px;
        border-radius: 5px 5px 0 0;
    }
}

#profile-info-wrapper {
    text-align: center;
    margin: 0;
    margin-top: 150px;
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    display: flex;

    @screen lg {
        margin-left: 60px;
        margin-top: 40px;
        border-bottom: 1px solid #e3e3e3;
        padding-bottom: 20px;
        margin-right: 50px;
        text-align: left;
    }
}

#progress-bar-outer {
    margin-top: 7px;
    position: relative;
    height: 7px;
    background: #e8e8e8;
    border-radius: 6px;

    #progress-bar-inner {
        transition: width .7s ease-in-out;
        position: absolute;
        height: 7px;
        top: 0;
        left: 0;
        width: 0;
        background: #ff9800;
        border-radius: 6px;
    }
}

#profile-hero-wrapper .leaderboard-1:after,
#profile-hero-wrapper .leaderboard-2:after,
#profile-hero-wrapper .leaderboard-3:after {
    content: "";
    position: absolute;
    color: #fff;
    top: -3px;
    right: -3px;
    width: 12px;
    height: 12px;
    border-radius: 100%;
    text-align: center;
    font-weight: 600;
    font-size: 10px
}

#profile-hero-wrapper .leaderboard-1:after {
    background: gold
}

#profile-hero-wrapper .leaderboard-2:after {
    background: silver
}

#profile-hero-wrapper .leaderboard-3:after {
    background: #cd7f32
}

.follow-btn {
    .normal {
        display: block;
    }

    .hover {
        display: none;
    }

    &:hover {
        .normal {
            display: none;
        }

        .hover {
            display: block;
        }
    }
}
</style>